import { CONTAINER_HEADING, METRIC_SIGNALS, MODULE_HEADING } from './constant';
import { resolveBoundaryModule } from './moduleMapping';
import type { SentryIssueHeadingInput, SentryModule } from './types';

export { MODULE_HEADING, METRIC_SIGNALS } from './constant';
export type { SentryIssueHeadingInput } from './types';

const errorText = (error: unknown): string => {
  if (error instanceof Error) return error.message?.trim() || error.name;
  if (typeof error === 'string') return error.trim();
  return 'Unknown error';
};

const areaLabel = (
  module?: SentryModule,
  componentName?: string,
  pathname?: string,
): string => {
  const resolved = resolveBoundaryModule(module, componentName, pathname);
  if (resolved) return MODULE_HEADING[resolved] ?? resolved.replace(/_/g, ' ');
  if (componentName) {
    return CONTAINER_HEADING[componentName] ?? componentName.replace(/_/g, ' ');
  }
  return 'Dashboard';
};

const kindLabel = (input: SentryIssueHeadingInput): string => {
  if (input.crashType === 'app_shell_crash') return 'App crash';
  if (input.crashType === 'container_section_crash') return 'Section crash';
  if (input.crashOrigin === 'api') return 'API error';
  if (input.crashOrigin === 'federated') return 'Federated module error';
  if (input.crashOrigin === 'chunk') return 'Chunk load error';
  if (input.label === 'auth' || input.label?.includes('signup')) return 'Auth error';
  if (input.label === 'botpress') return 'Chat error';
  if (input.label === 'feature-toggle') return 'Feature toggle error';
  return 'Error';
};

/** Shared FormController name — heading should use the route module, not "Payments". */
const GENERIC_CONTAINER_NAMES = new Set(['TRANSACTIONS']);

const cardOrFeatureLabel = (
  input: SentryIssueHeadingInput,
  area: string,
): string => {
  if (input.componentName && GENERIC_CONTAINER_NAMES.has(input.componentName)) {
    return '';
  }
  if (input.componentName) {
    const heading =
      CONTAINER_HEADING[input.componentName] ??
      input.componentName.replace(/_/g, ' ');
    if (heading === area) return '';
    return ` · ${heading}`;
  }
  if (input.feature) return ` · ${input.feature.replace(/_/g, ' ')}`;
  return '';
};

export const buildIssueHeading = (
  error: unknown,
  input: SentryIssueHeadingInput = {},
): string => {
  const area = areaLabel(input.module, input.componentName, input.pathname);
  const kind = kindLabel(input);
  const cause = errorText(error);
  const cardOrFeature = cardOrFeatureLabel(input, area);
  const endpoint = input.apiEndpoint
    ? ` (${input.apiEndpoint.split('?')[0]})`
    : '';
  const atPath = input.pathname?.trim() ? ` @ ${input.pathname.trim()}` : '';

  return `[${area}] ${kind}${cardOrFeature}${endpoint}${atPath} — ${cause}`;
};

/** Sentry issue title uses exception type + message — set both for a clear heading */
export const toSentryException = (
  error: unknown,
  heading: string,
): Error => {
  const original =
    error instanceof Error ? error : new Error(errorText(error));
  const separator = ' — ';
  const splitAt = heading.indexOf(separator);
  const title =
    splitAt >= 0 ? heading.slice(0, splitAt) : heading.slice(0, 120);
  const message =
    splitAt >= 0 ? heading.slice(splitAt + separator.length) : original.message;

  const display = new Error(message || original.message);
  display.name = title;
  display.stack = original.stack;
  return display;
};

export const isMetricOnlyEvent = (
  label?: string,
  hasException?: boolean,
): boolean => !hasException || (label != null && METRIC_SIGNALS.has(label));